import { Product } from 'shared/types/product/Product';
import { mapProduct } from './map-product';

/**
 * "Product IDs" variant of the V2 product slider and grid (`slider_type` / `grid_type` = 'Product IDs'):
 * the CMS author lists variant item-codes by hand, and the block shows exactly those variants in exactly
 * that order. Used by the server preload (to build the filter) and by the slider and grid views.
 */

/** CMS `product_ids`: comma-separated variant item-codes, e.g. `"96331B4E, 98611C"`. */
export const parseSkuCodes = (raw?: string): string[] =>
  raw
    ?.split(',')
    .map((code) => code.trim())
    .filter(Boolean) ?? [];

/** Algolia `filters` matching any listed variant (Algolia-facing `searchableAttributes` spelling). */
export const skuCodesFilter = (skuCodes: string[]): string =>
  skuCodes.map((sku) => `variants.searchableAttributes.item-code:'${sku}'`).join(' OR ');

/**
 * The block's products in the author's order. Each hit is mapped with the listed variant selected, so the
 * tile shows that variant (colour, image, code, link). Hits matching none of the codes are dropped, and a
 * product listed under several codes appears once, at its first listed position.
 *
 * Deliberately NOT the index's new-first order: the author's list is an editorial choice.
 */
export const productsInSkuOrder = (hits: any[], skuCodes: string[], locale: string): Product[] =>
  hits
    .map((hit) => {
      const order = skuCodes.findIndex((code) =>
        (hit?.variants ?? []).some((variant: any) => variant?.attributes?.['item-code'] === code),
      );
      return order === -1 ? null : { order, product: mapProduct(hit, locale, skuCodes[order]) };
    })
    .filter((entry): entry is { order: number; product: Product } => entry !== null)
    .sort((a, b) => a.order - b.order)
    .map(({ product }) => product);
